<template>
    <Toast />
    <ConfirmDialog />
    <div class="grid">
        <div class="col-12">
            <div class="card">
                <div class="flex flex-column md:flex-row md:justify-content-between md:align-items-center mb-3">
                    <div>
                        <h5 class="m-0">Conteo de Inventario</h5>
                        <small class="text-color-secondary">Cuente lo que hay físicamente. El sistema compara al guardar.</small>
                    </div>
                    <Button v-if="isManager" label="Ver historial" icon="pi pi-list" class="p-button-text mt-2 md:mt-0" @click="$router.push('/dashboard/inventario/conteo/historial')" />
                </div>

                <ProgressBar v-if="loading" mode="indeterminate" style="height: 4px" class="mb-3" />

                <Steps v-if="!loading && preview" :model="stepsModel" :readonly="true" :data-active="currentStep" class="mb-4 count-steps" />

                <!-- STEP 1: Inicio -->
                <div v-if="!loading && preview && currentStep === 0">
                    <div class="grid">
                        <div class="col-12 md:col-6">
                            <div class="surface-100 border-round p-3 h-full">
                                <div class="text-color-secondary text-sm">Realizado por</div>
                                <div class="font-medium mt-1">{{ userLabel }}</div>
                            </div>
                        </div>
                        <div class="col-12 md:col-6">
                            <div class="surface-100 border-round p-3 h-full">
                                <label for="category" class="text-color-secondary text-sm block mb-1">Qué contar</label>
                                <Dropdown id="category" v-model="categoryId" :options="categoryOptions" optionLabel="name" optionValue="id"
                                          class="w-full" @change="reload" />
                            </div>
                        </div>
                    </div>

                    <Message severity="info" :closable="false" class="mt-3">
                        Cuente las unidades que hay ahora mismo y anótelas tal cual, aunque sea cero. Lo que deje en blanco
                        queda como <b>No contado</b>. Si es posible, cuente sin ventas en curso: las que ocurran mientras
                        cuenta se marcan en el resultado.
                    </Message>

                    <div class="flex justify-content-between align-items-center mt-4">
                        <span class="text-color-secondary text-sm">{{ lines.length }} producto(s) para contar</span>
                        <Button label="Empezar a contar" icon="pi pi-arrow-right" iconPos="right" :disabled="lines.length === 0" @click="currentStep = 1" />
                    </div>
                    <Message v-if="lines.length === 0" severity="warn" :closable="false" class="mt-3">
                        No hay productos con inventario para contar.
                    </Message>
                </div>

                <!-- STEP 2: Conteo -->
                <div v-if="!loading && preview && currentStep === 1">
                    <div class="flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-2 mb-3">
                        <span class="p-input-icon-left">
                            <i class="pi pi-search" />
                            <InputText v-model.trim="search" placeholder="Buscar producto..." />
                        </span>
                        <div class="text-sm">
                            <b>{{ progress.counted }}</b> de {{ progress.total }} contados
                            <ProgressBar :value="progressPercent" :showValue="false" style="height: 6px; width: 12rem" class="mt-1" />
                        </div>
                    </div>

                    <div v-for="group in visibleGroups" :key="group.name" class="mb-4">
                        <div class="font-bold text-sm text-color-secondary uppercase mb-2">{{ group.name }}</div>
                        <div v-for="line in group.lines" :key="line.productId" class="count-row">
                            <div class="flex-1">
                                <div class="font-medium">{{ line.name }}</div>
                                <small v-if="line.code" class="text-color-secondary">{{ line.code }}</small>
                            </div>
                            <InputNumber
                                v-model="line.physicalStock"
                                :id="'physical_' + line.productId"
                                mode="decimal"
                                :min="0"
                                :minFractionDigits="0"
                                :maxFractionDigits="3"
                                locale="en-US"
                                :useGrouping="false"
                                placeholder="Sin contar"
                                inputClass="count-input"
                                @input="onPhysicalInput(line, $event)"
                            />
                        </div>
                    </div>
                    <div v-if="visibleGroups.length === 0" class="text-color-secondary text-sm py-3"><i>Ningún producto coincide con la búsqueda.</i></div>

                    <div class="field mt-3">
                        <label for="notes" class="block">Notas (opcional)</label>
                        <Textarea id="notes" v-model="notes" rows="2" class="w-full" placeholder="Observaciones del conteo..." />
                    </div>

                    <div class="flex justify-content-between mt-4">
                        <Button label="Atrás" icon="pi pi-arrow-left" class="p-button-text" @click="currentStep = 0" />
                        <Button label="Guardar conteo" icon="pi pi-check" class="p-button-success" :loading="saving"
                                :disabled="progress.counted === 0" @click="confirmSave" />
                    </div>
                </div>

                <!-- STEP 3: Resultado (siempre desde la respuesta del servidor) -->
                <div v-if="!loading && saved && currentStep === 2">
                    <div class="text-center my-4">
                        <i class="pi pi-check-circle text-green-500" style="font-size: 3.5rem"></i>
                        <h4 class="mt-3 mb-1">Conteo #{{ saved.id }} guardado</h4>
                        <p class="text-color-secondary m-0">El stock del sistema no cambió. El administrador revisa el conteo y decide si aplica los ajustes.</p>
                        <Tag :value="resultLabel(saved.result)" :severity="resultSeverity(saved.result)" class="mt-2" />
                    </div>

                    <div class="grid text-center mb-3">
                        <div class="col-6 md:col-3"><div class="surface-100 border-round p-3"><div class="text-sm text-color-secondary">Cuadrados</div><div class="font-bold text-xl text-green-500">{{ saved.balancedCount }}</div></div></div>
                        <div class="col-6 md:col-3"><div class="surface-100 border-round p-3"><div class="text-sm text-color-secondary">Sobrantes</div><div class="font-bold text-xl text-blue-500">{{ saved.overageCount }}</div></div></div>
                        <div class="col-6 md:col-3"><div class="surface-100 border-round p-3"><div class="text-sm text-color-secondary">Faltantes</div><div class="font-bold text-xl text-red-500">{{ saved.shortageCount }}</div></div></div>
                        <div class="col-6 md:col-3"><div class="surface-100 border-round p-3"><div class="text-sm text-color-secondary">No contados</div><div class="font-bold text-xl">{{ saved.notCountedCount }}</div></div></div>
                    </div>

                    <DataTable :value="saved.lines" responsiveLayout="scroll" class="p-datatable-sm">
                        <Column field="productName" header="Producto">
                            <template #body="{ data }">
                                {{ data.productName }}
                                <i v-if="data.movedDuringCount" class="pi pi-clock text-orange-500 ml-1" v-tooltip.top="'Hubo ventas o compras mientras se contaba'"></i>
                            </template>
                        </Column>
                        <Column field="categoryName" header="Categoría" />
                        <Column header="Sistema">
                            <template #body="{ data }">{{ formatQty(data.theoreticalStock) }}</template>
                        </Column>
                        <Column header="Físico">
                            <template #body="{ data }">{{ formatQty(data.physicalStock) }}</template>
                        </Column>
                        <Column header="Diferencia">
                            <template #body="{ data }"><span :class="diffClass(data.difference)">{{ formatSignedQty(data.difference) }}</span></template>
                        </Column>
                        <Column header="Resultado">
                            <template #body="{ data }"><Tag :value="resultLabel(data.result)" :severity="resultSeverity(data.result)" /></template>
                        </Column>
                        <Column v-if="isManager" header="Valor">
                            <template #body="{ data }">
                                <span v-if="data.valueDifference !== null && data.valueDifference !== undefined" :class="diffClass(data.valueDifference)">{{ formatMoney(data.valueDifference) }}</span>
                            </template>
                        </Column>
                    </DataTable>

                    <div class="flex justify-content-center gap-2 mt-4">
                        <Button label="Nuevo conteo" icon="pi pi-refresh" class="p-button-outlined" @click="restart" />
                        <Button v-if="isManager" label="Revisar y aplicar" icon="pi pi-eye" @click="$router.push({ path: '/dashboard/inventario/conteo/detalle', query: { id: saved.id } })" />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import httpRequest from '../../../service/httpRequest';
import {
    buildCountPayload, clearDraft, countProgress, diffClass, draftKey, formatMoney, formatQty,
    formatSignedQty, groupByCategory, loadDraft, resultLabel, resultSeverity, saveDraft,
} from './inventoryFormat';

const ALL = 0;

export default {
    name: 'InventoryCountWizard',
    data() {
        return {
            loading: false,
            saving: false,
            currentStep: 0,
            preview: null,
            categoryId: ALL,
            lines: [],
            search: '',
            notes: '',
            saved: null,
            stepsModel: [{ label: 'Inicio' }, { label: 'Conteo' }, { label: 'Resultado' }],
        };
    },
    created() {
        this.reload();
    },
    computed: {
        isManager() {
            // Espejo del backend: ver dinero, historial y aplicar es de quien gestiona el inventario.
            return this.$can('inventoryManage');
        },
        userLabel() {
            const user = this.$store.getters.getUser;
            if (!user) return '-';
            return user.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : user.username;
        },
        categoryOptions() {
            const categories = (this.preview && this.preview.categories) || [];
            return [{ id: ALL, name: 'Todos los productos' }, ...categories];
        },
        progress() {
            return countProgress(this.lines);
        },
        progressPercent() {
            return this.progress.total ? Math.round(100 * this.progress.counted / this.progress.total) : 0;
        },
        visibleGroups() {
            const term = (this.search || '').toLowerCase();
            const lines = term
                ? this.lines.filter(l => (l.name || '').toLowerCase().includes(term) || (l.code || '').toLowerCase().includes(term))
                : this.lines;
            return groupByCategory(lines);
        },
        draftStorageKey() {
            const user = this.$store.getters.getUser || {};
            const activity = this.$store.getters.defaultActivity || {};
            return draftKey(user.id || user.username, activity.id) + ':' + (this.categoryId || ALL);
        },
    },
    watch: {
        lines: {
            deep: true,
            handler(lines) {
                if (!this.saved) saveDraft(window.localStorage, this.draftStorageKey, lines);
            },
        },
    },
    methods: {
        formatQty, formatSignedQty, formatMoney, diffClass, resultLabel, resultSeverity,
        async reload() {
            this.loading = true;
            try {
                const r = await httpRequest.getInventoryCountPreview(this.categoryId || null);
                if (r.status === 200 && r.data) {
                    this.preview = r.data;
                    const draft = loadDraft(window.localStorage, this.draftStorageKey);
                    this.lines = (r.data.products || []).map(p => ({
                        ...p,
                        physicalStock: draft[p.productId] !== undefined ? draft[p.productId] : null,
                    }));
                } else {
                    this.$toast.add({ severity: 'error', summary: 'Error', detail: r.error || 'No se pudo cargar el conteo', life: 4000 });
                }
            } catch (e) {
                console.error(e);
                this.$toast.add({ severity: 'error', summary: 'Error', detail: 'Fallo de red', life: 4000 });
            } finally {
                this.loading = false;
            }
        },
        onPhysicalInput(line, event) {
            // InputNumber emite `input` antes de actualizar el v-model: así el progreso
            // se mueve mientras se escribe y no recién al salir del campo.
            line.physicalStock = event.value;
        },
        confirmSave() {
            const missing = this.progress.total - this.progress.counted;
            const message = missing > 0
                ? `Hay ${missing} producto(s) sin contar: quedarán como "No contado". ¿Guardar el conteo?`
                : '¿Guardar el conteo con estas cantidades?';
            this.$confirm.require({
                message,
                header: 'Confirmar conteo',
                icon: 'pi pi-exclamation-triangle',
                acceptLabel: 'Guardar',
                rejectLabel: 'Seguir contando',
                accept: () => this.save(),
            });
        },
        async save() {
            this.saving = true;
            try {
                const r = await httpRequest.saveInventoryCount(
                    buildCountPayload(this.lines), this.notes, this.categoryId || null,
                    this.preview && this.preview.sessionToken);
                if (r.status === 200 && r.data) {
                    this.saved = r.data;
                    clearDraft(window.localStorage, this.draftStorageKey);
                    this.currentStep = 2;
                    this.$toast.add({ severity: 'success', summary: 'Conteo guardado', detail: `Conteo #${r.data.id} registrado`, life: 4000 });
                } else {
                    this.$toast.add({ severity: 'error', summary: 'Error', detail: r.error || 'No se pudo guardar el conteo', life: 5000 });
                }
            } catch (e) {
                console.error(e);
                this.$toast.add({ severity: 'error', summary: 'Error', detail: 'Fallo de red', life: 4000 });
            } finally {
                this.saving = false;
            }
        },
        restart() {
            this.currentStep = 0;
            this.saved = null;
            this.notes = '';
            this.search = '';
            this.reload();
        },
    },
};
</script>

<style scoped>
.count-row {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 0.6rem 0;
    border-bottom: 1px solid var(--surface-200);
}
.count-row :deep(.count-input) {
    width: 8rem;
    text-align: right;
}
.count-steps :deep(.p-steps-item .p-steps-number) {
    background: var(--surface-200);
    color: var(--text-color-secondary);
    border: 2px solid var(--surface-300);
}
.count-steps[data-active="0"] :deep(.p-steps-item:nth-child(1) .p-steps-number),
.count-steps[data-active="1"] :deep(.p-steps-item:nth-child(-n+2) .p-steps-number),
.count-steps[data-active="2"] :deep(.p-steps-item:nth-child(-n+3) .p-steps-number) {
    background: var(--primary-color);
    color: var(--primary-color-text);
    border-color: var(--primary-color);
}
.count-steps[data-active="0"] :deep(.p-steps-item:nth-child(1) .p-steps-title),
.count-steps[data-active="1"] :deep(.p-steps-item:nth-child(-n+2) .p-steps-title),
.count-steps[data-active="2"] :deep(.p-steps-item:nth-child(-n+3) .p-steps-title) {
    color: var(--text-color);
    font-weight: 600;
}
</style>
